import React, { useCallback, useContext } from 'react';
import Promise from 'bluebird';
import { Body1Strong, Field, mergeClasses, Button } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';


import { HelpPopup } from '@bingads-webui-react/help-popup';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Tags } from '@bingads-webui-fluent-component/tags';

import { useFetchingState } from './hooks/use-fetching-state';
import { usePaginatedFetchingState } from './hooks/use-paginated-fetching-state';
import { SearchableMultiSelectorCombobox } from './components/combobox';
import { BaseResultType } from './types';
import { useSearchableMultiSelectorStyles } from './components/styles';

export interface SearchableMultiSelectorInputProps<ResultType> {
  className?: string;
  label: string;
  helpTopic?: string;
  helpLabel?: string;
  disabled?: boolean;
  multiSelect?: boolean;
  disableTags?: boolean;
  fetchOnEmptyInput?: boolean;
  allowSelectAll?: boolean;
  allowRemoveAll?: boolean;
  loadingText?: string;
  tooltipText?: string;
  placeholderText?: string;
  noResultsText?: string;
  resultsTitle?: string;
  tagViewLimit?: number;
  fetchFn: (query: string, pageNumber?: number, pageSize?: number) => Promise<{ results: ResultType[], totalCount: number } | ResultType[]>;
  value: ResultType[];
  pageSize?: number;
  onChange: (value: (ResultType)[]) => void;
  onRenderOption?: (option: ResultType) => React.ReactNode;
  onRenderLabel?: () => React.ReactNode;
}


export const SearchableMultiSelectorInput = <ResultType extends BaseResultType, >({
  className,
  label,
  helpTopic,
  helpLabel,
  disabled,
  multiSelect,
  disableTags,
  fetchOnEmptyInput,
  allowSelectAll,
  allowRemoveAll,
  loadingText,
  placeholderText,
  tooltipText,
  noResultsText,
  resultsTitle,
  tagViewLimit,
  fetchFn,
  value,
  pageSize,
  onChange,
  onRenderOption,
  onRenderLabel,
}: SearchableMultiSelectorInputProps<ResultType>) => {
  const classes = useSearchableMultiSelectorStyles();
  const i18n = useContext(I18nContext) as any;
  const paginatedFetchingState = usePaginatedFetchingState({ fetchFn, fetchOnEmptyInput, pageSize });
  const fetchingState = useFetchingState({ fetchFn, fetchOnEmptyInput });

  const {
    status,
    results,
    fetch,
    onCleanUp,
    hasMore,
    fetchNextPage,
  } = pageSize ? paginatedFetchingState : { ...fetchingState, hasMore: undefined, fetchNextPage: () => {} };

  const tagItems = value.map(({ id }) => id);

  const onRenderTagContent = useCallback((id) => {
    const result = value.find(({ id: resultId }) => resultId === id);
    return (<div>{result?.name}</div>
    );
  }, [value]);

  const onChangeTagItems = useCallback((newTagItems) => {
    const newSelected = value.filter(({ id }) => newTagItems.includes(id));
    onChange(newSelected);
  }, [onChange, value]);

  const addItem = useCallback(newItem =>
    onChange(value.concat(newItem)), [onChange, value]);

  const removeItem = useCallback(removedId =>
    onChange(value.filter(({ id }) => id !== removedId)), [onChange, value]);

  const toggleOption = useCallback((newSelected) => {
    const isAlreadySelected = value.some(({ id }) => id === newSelected.id);

    if (isAlreadySelected) {
      removeItem(newSelected.id);
    } else {
      addItem(newSelected);
    }
  }, [addItem, removeItem, value]);

  const selectAllOptions = useCallback(() => {
    const newSelected = results.filter(({ id }) => !value.some(({ id: selectedId }) => id === selectedId));
    onChange(value.concat(newSelected));
  }, [value, results, onChange]);

  const removeAllSelected = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const renderLabelWithHelpButton = useCallback(() => (
    <div className={classes.labelContent}>
      <Body1Strong className="searchable-multi-selector-input-label">{label}</Body1Strong>
      {helpTopic && <HelpPopup topic={helpTopic} aria-label={helpLabel} />}
    </div>
  ), [label, helpTopic, helpLabel, classes.labelContent]);

  const renderLabel = useCallback(() => (
    <div className={classes.labelContainer}>
      {onRenderLabel ? onRenderLabel() : renderLabelWithHelpButton()}

      {allowRemoveAll && tagItems.length > 0 && (
        <Button
          className={mergeClasses('remove-all-button', classes.removeAllButton)}
          onClick={removeAllSelected}
          appearance="transparent"
          style={{ padding: 0 }}
          aria-label={i18n.getString(_TL_('Remove all'))}
          icon={<DeleteRegular />}
        >
          {i18n.getString(_TL_('Remove all'))}
        </Button>
        )}
    </div>
  ), [classes.labelContainer, classes.removeAllButton, onRenderLabel, renderLabelWithHelpButton, allowRemoveAll, removeAllSelected, i18n, tagItems.length]);

  return (
    <div className={mergeClasses('searchable-multi-selector-input', className)}>
      <Field label={{ children: renderLabel }}>
        {
          !disableTags &&
            (<Tags
              tagItems={tagItems}
              onRenderTagContent={onRenderTagContent}
              i18n={i18n}
              onChange={onChangeTagItems}
              tagViewLimit={tagViewLimit}
            />)
        }
        <SearchableMultiSelectorCombobox
          selected={value}
          disabled={disabled}
          status={status}
          hasMore={hasMore}
          results={results}
          loadingText={loadingText}
          placeholderText={placeholderText}
          noResultsText={noResultsText}
          tooltipText={tooltipText}
          onChangeQuery={fetch}
          onSelectOption={toggleOption}
          // @ts-ignore
          onRenderOption={onRenderOption}
          multiSelect={multiSelect}
          resultsTitle={resultsTitle}
          onCleanUp={onCleanUp}
          allowSelectAll={allowSelectAll}
          onSelectAll={selectAllOptions}
          onFetchMore={fetchNextPage}
        />
      </Field>
    </div>
  );
};

SearchableMultiSelectorInput.defaultProps = {
  className: undefined,
  helpTopic: undefined,
  helpLabel: undefined,
  loadingText: undefined,
  placeholderText: undefined,
  noResultsText: undefined,
  tooltipText: undefined,
  disabled: false,
  disableTags: false,
  fetchOnEmptyInput: false,
  multiSelect: true,
  onRenderOption: undefined,
  allowSelectAll: false,
  allowRemoveAll: false,
  resultsTitle: undefined,
  tagViewLimit: undefined,
  onRenderLabel: undefined,
  pageSize: undefined,
};
